import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workApiFetch, workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { WorkOrderId } from "../lib/workIds";

export type WorkOrderTimeEntryInput = {
  workDate: string;
  durationMinutes: number;
  note?: string | null;
};

// The contributor is chosen once, when the entry is created. Omitting it logs
// the time for the caller.
export type CreateWorkOrderTimeEntryInput = WorkOrderTimeEntryInput & {
  userId?: number;
};

async function errorMessage(response: Response, fallback: string) {
  const body = (await response.json().catch(() => null)) as {
    error?: string;
  } | null;
  return body?.error ?? fallback;
}

export function useWorkOrderTimeEntryMutations(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();

  const invalidate = async () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["work-order-time-entries"] }),
      queryClient.invalidateQueries({ queryKey: ["work-order-detail"] }),
      queryClient.invalidateQueries({ queryKey: ["work-orders"] }),
    ]);

  const mutationContext = () => {
    if (!selectedCustomer || !selectedFacility) {
      throw new Error("A customer and facility must be selected.");
    }
    return {
      customerId: selectedCustomer.id,
      facilityId: selectedFacility.id,
      workOrderId,
    };
  };

  const create = useMutation({
    mutationFn: async (input: CreateWorkOrderTimeEntryInput) => {
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"]["time-entries"].$post({
        param: mutationContext(),
        json: input,
      });
      if (!response.ok) {
        throw new Error(
          await errorMessage(response, "Unable to add time entry."),
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Time entry added.", { icon: true });
      await invalidate();
    },
    onError: (error) =>
      toast.add(
        error instanceof Error ? error.message : "Unable to add time entry.",
      ),
  });

  const update = useMutation({
    mutationFn: async ({
      id,
      ...input
    }: WorkOrderTimeEntryInput & { id: string }) => {
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"]["time-entries"][":timeEntryId"].$patch({
        param: { ...mutationContext(), timeEntryId: id },
        json: input,
      });
      if (!response.ok) {
        throw new Error(
          await errorMessage(response, "Unable to update time entry."),
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Time entry updated.", { icon: true });
      await invalidate();
    },
    onError: (error) =>
      toast.add(
        error instanceof Error ? error.message : "Unable to update time entry.",
      ),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { customerId, facilityId } = mutationContext();
      const response = await workApiFetch(
        `/v1/customers/${encodeURIComponent(customerId)}/facilities/${encodeURIComponent(facilityId)}/work-orders/${encodeURIComponent(workOrderId)}/time-entries/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      );
      if (!response.ok) {
        throw new Error(
          await errorMessage(response, "Unable to delete time entry."),
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Time entry deleted.", { icon: true });
      await invalidate();
    },
    onError: (error) =>
      toast.add(
        error instanceof Error ? error.message : "Unable to delete time entry.",
      ),
  });

  return { create, update, remove };
}
